import {
  EnumDiscountType,
  EnumDiscountUnit,
  EnumOrderSourceType,
  EnumProductType,
  OrderByIdQuery,
  OrderPricesByIdQuery,
  RenewablePurchaseByIdQuery,
} from "gql/generated";
import { formatMoney } from "shared/utils/formatMoney";
import { formatPrice } from "shared/utils/formatPrice";
import { getMultilingualString } from "shared/utils/getMultilingualString";
import { getPerTicketDiscountCount } from "shared/utils/getDiscountInfoStrings";
import { CartItem } from "widgets/booker/contexts/CartContextProvider";
import { type DiscountItem } from "widgets/booker/shared/CheckoutSummary/DiscountItem";
import type { PaymentRequestLineItem } from "widgets/booker/contexts/BookerContextProvider/BookerContext";

type RenewablePurchase =
  | RenewablePurchaseByIdQuery["renewablePurchaseById"]
  | {
      selectedTier?: {
        tierId?: string | null;
        tierName?: { en?: string | null; es?: string | null; fr?: string | null } | null;
        price?: number | null;
      } | null;
      originalChargeAmount?: number | null;
      appliedDiscount?: {
        name?: string | null;
        promoCode?: string | null;
        unit?: EnumDiscountUnit | string | null;
        amount?: number | null;
        discountAmount?: number | null;
      } | null;
    }
  | null
  | undefined;

function resolveRenewableDiscountAmount(
  renewablePurchase: NonNullable<RenewablePurchase>,
): number {
  const explicit = renewablePurchase.appliedDiscount?.discountAmount;
  if (typeof explicit === "number" && explicit > 0) return explicit;

  const tierPrice = renewablePurchase.selectedTier?.price;
  const charged = renewablePurchase.originalChargeAmount;
  if (
    typeof tierPrice === "number" &&
    typeof charged === "number" &&
    tierPrice > charged
  ) {
    return tierPrice - charged;
  }
  return 0;
}

function resolveRenewableDiscountUnit(
  renewablePurchase: NonNullable<RenewablePurchase>,
): EnumDiscountUnit | string | null | undefined {
  return renewablePurchase.appliedDiscount?.unit;
}

function resolveRenewableDiscountRate(
  renewablePurchase: NonNullable<RenewablePurchase>,
): number | null | undefined {
  return renewablePurchase.appliedDiscount?.amount;
}

function resolveRenewableDiscountLabel(
  renewablePurchase: NonNullable<RenewablePurchase>,
): string {
  const applied = renewablePurchase.appliedDiscount;
  if (applied?.name?.trim()) return applied.name.trim();
  if (applied?.promoCode?.trim()) return applied.promoCode.trim();
  return "Discount";
}

type Order = NonNullable<OrderByIdQuery["orderById"]>;
type OrderPrices = NonNullable<OrderPricesByIdQuery["orderPricesById"]>;

function isAutoChargeRenewalOrder(order: Order): boolean {
  return (
    order.source?.type === EnumOrderSourceType.Renewal &&
    !!order.source.renewalRecordId &&
    !order.source.customerInvoicePayment
  );
}

function mapOrderDiscounts(
  order: Order,
  orderPrices: OrderPrices,
  offTranslation: string,
  perTicketTranslation?: string,
  flatRateTranslation?: string,
): DiscountItem[] {
  return (order.discounts ?? [])
    .filter((discount): discount is NonNullable<typeof discount> => !!discount)
    .map(discount => {
      const total =
        orderPrices.discountTotals.find(d => d.discountId === discount._id)
          ?.total ?? 0;
      return {
        label:
          getDiscountLabel(discount.name, discount.type, discount.promoCode) +
          " | " +
          getDiscountUnitLabel(discount.unit, discount.amount, offTranslation, {
            perTicket: !!discount.perTicket,
            perTicketTranslation,
            flatRateTranslation,
            ticketCount: getPerTicketDiscountCount(discount, total),
          }),
        total,
        type: discount.type,
        isMainProductOnly: !!discount.excludeUpsells,
      };
    });
}

function getDiscountLabel(
  name: { en?: string | null; es?: string | null; fr?: string | null },
  type: EnumDiscountType,
  promoCode?: string | null,
): string {
  if (type === EnumDiscountType.PromoCode && promoCode?.trim()) {
    return promoCode.trim();
  }
  return getMultilingualString(name);
}

function getDiscountUnitLabel(
  unit: EnumDiscountUnit,
  amount: number,
  offTranslation: string,
  options?: {
    ticketCount?: number;
    perTicket?: boolean;
    perTicketTranslation?: string;
    flatRateTranslation?: string;
  },
): string {
  let unitInfo: string;
  if (unit === EnumDiscountUnit.Percentage) {
    unitInfo = `${amount * 100}% ${offTranslation}`;
  } else if (unit === EnumDiscountUnit.FlatRate) {
    // `amount` is the target price, not an amount off.
    unitInfo = `$${formatPrice(amount)} ${options?.flatRateTranslation ?? "flat rate"}`;
  } else {
    // Match checkout line totals — plain `$`, no CA/US currency prefix.
    unitInfo = `$${formatPrice(amount)} ${offTranslation}`;
  }
  if (options?.perTicket) {
    unitInfo = `${unitInfo} ${options.perTicketTranslation ?? "per ticket"}`;
  }
  if (options?.ticketCount != null && options.ticketCount > 0) {
    return `${unitInfo} × ${options.ticketCount}`;
  }
  return unitInfo;
}

function buildRenewableDiscountItem(
  renewablePurchase: NonNullable<RenewablePurchase>,
  offTranslation: string,
): DiscountItem | null {
  const discountAmount = resolveRenewableDiscountAmount(renewablePurchase);
  if (discountAmount <= 0) return null;

  const unit = resolveRenewableDiscountUnit(renewablePurchase);
  const rate = resolveRenewableDiscountRate(renewablePurchase);
  const discountLabel = resolveRenewableDiscountLabel(renewablePurchase);

  const labelParts = [
    discountLabel,
    unit === EnumDiscountUnit.Percentage && typeof rate === "number"
      ? `${rate * 100}% ${offTranslation}`
      : typeof rate === "number"
        ? `${formatMoney(rate)} ${offTranslation}`
        : null,
  ].filter(Boolean);

  return {
    label: labelParts.join(" | "),
    total: discountAmount,
    type: EnumDiscountType.PromoCode,
    isMainProductOnly: true,
  };
}

function isMainProductLineItem(
  item: { description?: string | null; amount?: number | null },
  cart: CartItem[],
  chargedSubtotal?: number,
): boolean {
  if (cart.length === 0) return false;
  const mainName = cart[0]?.productName?.trim().toLowerCase();
  const desc = item.description?.trim().toLowerCase();
  if (mainName && desc && (desc === mainName || desc.includes(mainName))) {
    return true;
  }
  if (
    typeof chargedSubtotal === "number" &&
    typeof item.amount === "number" &&
    item.amount === chargedSubtotal
  ) {
    return true;
  }
  return false;
}

export function buildOrderCartItems(
  order: Order,
  orderPrices: OrderPrices,
  renewablePurchase?: RenewablePurchase,
): CartItem[] {
  const isRenewalOrder = isAutoChargeRenewalOrder(order);
  const renewalLine = order.additionalLineItems?.[0];
  const snapshotLineItems = (order.pricingSnapshot?.lineItems ?? []).filter(
    (line): line is NonNullable<typeof line> => !!line,
  );

  return (order.purchasedProducts ?? [])
    .filter(
      (purchasedProduct): purchasedProduct is NonNullable<typeof purchasedProduct> =>
        !!purchasedProduct,
    )
    .map((purchasedProduct, idx) => {
      const baseName = purchasedProduct.product.name.en ?? "";

      const isEvent = purchasedProduct.product.type === EnumProductType.Event;
      const eventSessionIds = (purchasedProduct.sessionIds ?? []).filter(
        (id): id is string => Boolean(id),
      );
      const eventStartDate = eventSessionIds[0]?.split("::")[0] ?? "";
      const merchVariant = purchasedProduct.merchVariantId
        ? purchasedProduct.product.merchMetadata?.variants?.find(
            v => v?._id === purchasedProduct.merchVariantId,
          )
        : undefined;

      let productName = baseName;
      if (idx === 0 && isRenewalOrder && renewalLine?.description) {
        productName = renewalLine.description;
      }

      let lineTotal = orderPrices.productPrices[idx]?.price ?? 0;
      const tierFullPrice = renewablePurchase?.selectedTier?.price;
      const renewableDiscount = renewablePurchase
        ? resolveRenewableDiscountAmount(renewablePurchase)
        : 0;
      if (
        idx === 0 &&
        typeof tierFullPrice === "number" &&
        renewableDiscount > 0 &&
        !isRenewalOrder &&
        !order.source?.customerInvoicePayment
      ) {
        lineTotal = tierFullPrice;
      }

      return {
        productName,
        productId: purchasedProduct.product._id,
        productType: purchasedProduct.product.type,
        startDateISO: isEvent
          ? eventStartDate
          : purchasedProduct.inventoryClaims?.[0]?.claimedDateRangeISO?.start ??
            "",
        endDateISO: isEvent
          ? eventStartDate
          : purchasedProduct.inventoryClaims?.[0]?.claimedDateRangeISO?.end ??
            "",
        total: lineTotal,
        guestsPerTier: purchasedProduct.guestsPerTier,
        quantity: purchasedProduct.quantity,
        // Frozen at checkout; receipts render these verbatim. Renewal rows
        // keep the charged-description display instead.
        storedTierLines: isRenewalOrder
          ? []
          : snapshotLineItems.filter(
              line => line.productId === purchasedProduct.product._id,
            ),
        selectedTierId: isRenewalOrder
          ? null
          : (purchasedProduct.tierId ?? null),
        ...(isEvent && eventSessionIds.length > 0
          ? {
              sessionId: eventSessionIds[0],
              sessionIds: eventSessionIds,
            }
          : {}),
        ...(merchVariant
          ? {
              merchVariantId: purchasedProduct.merchVariantId,
              merchVariantLabel: merchVariant.label,
            }
          : {}),
      } as CartItem;
    });
}

export function buildOrderReceiptDiscounts(
  order: Order,
  orderPrices: OrderPrices,
  renewablePurchase: RenewablePurchase,
  offTranslation: string,
  perTicketTranslation?: string,
  flatRateTranslation?: string,
): DiscountItem[] {
  const fromOrder = mapOrderDiscounts(
    order,
    orderPrices,
    offTranslation,
    perTicketTranslation,
    flatRateTranslation,
  );
  if (fromOrder.length > 0) return fromOrder;
  // An invoice-paid renewal must mirror its frozen invoice exactly. Do not
  // synthesize a discount from the mutable purchase when the invoice had none.
  if (order.source?.customerInvoicePayment) return [];

  const fromRenewable = renewablePurchase
    ? buildRenewableDiscountItem(renewablePurchase, offTranslation)
    : null;
  return fromRenewable ? [fromRenewable] : [];
}

export function buildOrderAdditionalLineItems(
  order: Order,
  cart: CartItem[],
  renewablePurchase?: RenewablePurchase,
): PaymentRequestLineItem[] {
  const items = (order.additionalLineItems ?? []).filter(
    (item): item is NonNullable<typeof item> =>
      !!item && !!item.description?.trim(),
  );

  if (items.length === 0) return [];

  const isRenewalOrder = isAutoChargeRenewalOrder(order);

  // Renewal orders store the charged renewal on `additionalLineItems`; that
  // description is folded into the cart row above.
  if (isRenewalOrder) return [];

  const chargedSubtotal =
    renewablePurchase?.originalChargeAmount ??
    items.find(item => item.quantity === 1)?.amount ??
    undefined;

  return items
    .filter(item => !isMainProductLineItem(item, cart, chargedSubtotal))
    .map(item => ({
      description: item.description ?? "",
      amount: item.amount ?? 0,
      quantity: item.quantity ?? 1,
    }));
}

export type AppliedDiscountSummary = {
  label: string;
  amount: number;
};

/** Discount rule name shown as a variant on the membership tier line. */
export function buildPurchaseDetailsTierVariant(
  order: Order,
  orderPrices: OrderPrices | null | undefined,
  renewablePurchase: RenewablePurchase,
): string | null {
  const fromOrder = (order.discounts ?? [])
    .filter((discount): discount is NonNullable<typeof discount> => !!discount)
    .map(discount => ({
      label: getDiscountLabel(discount.name, discount.type, discount.promoCode),
      amount:
        orderPrices?.discountTotals.find(d => d.discountId === discount._id)
          ?.total ?? 0,
    }))
    .find(d => d.amount > 0);

  if (fromOrder?.label) return fromOrder.label;

  if (!renewablePurchase) return null;
  const discountAmount = resolveRenewableDiscountAmount(renewablePurchase);
  if (discountAmount <= 0) return null;

  const label = resolveRenewableDiscountLabel(renewablePurchase);
  return label === "Discount" ? null : label;
}

export function buildPurchaseDetailsDiscounts(
  order: Order,
  orderPrices: OrderPrices | null | undefined,
  renewablePurchase: RenewablePurchase,
): AppliedDiscountSummary[] {
  const fromOrder = (order.discounts ?? [])
    .filter((discount): discount is NonNullable<typeof discount> => !!discount)
    .map(discount => ({
      label: getDiscountLabel(discount.name, discount.type, discount.promoCode),
      amount:
        orderPrices?.discountTotals.find(d => d.discountId === discount._id)
          ?.total ?? 0,
    }))
    .filter(d => d.amount > 0);

  if (fromOrder.length > 0) return fromOrder;

  if (!renewablePurchase) return [];

  const discountAmount = resolveRenewableDiscountAmount(renewablePurchase);
  if (discountAmount <= 0) return [];

  return [
    {
      label: resolveRenewableDiscountLabel(renewablePurchase),
      amount: discountAmount,
    },
  ];
}
